import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { useForm, FormProvider } from 'react-hook-form';
import { StepButton, StepLabel, StepIcon } from '@mui/material';
import CustomStepIcon from '#component/shipment/form/custom-step-icon';
import { GO_TO_APPLIANCE_STEP, OPEN_BACKDROP, RESET_APPLIANCE_FORM_DATA, CLOSE_BACKDROP } from '#action/types';
import { goToApplianceStep } from '#action/warehouse/appliance/form';
import { reduxFormResolver } from '#helper/rhf-resolver';
import { Container, StyledStepper, StyledStep } from './styles';
import ApplianceForm from './appliance-tab';
import DevicesForm from './devices-tab';
import { getSubmitData } from './helper';
import { ErrorsContext } from './errors-context';
import validate from './validate';

const steps = [
  { label: 'Application', form: 'EditApplianceForm' },
  { label: 'Devices', form: 'DevicesApplianceForm' }
];

const APPLIANCE_STATUS = {
  STOPPED: 300
};

const Appliance = props => {
  const { handleFormSubmit, initialValues } = props;
  const [errors, setErrors] = useState({ devicesGroup: [] });
  const [forceError, setForceError] = useState(false);
  const { match: { params: { applianceId } } } = props;

  const step = useSelector(s => s.applianceForm.step);
  const appliance = useSelector(a => a.appliance.appliance, shallowEqual);
  const isApplianceStop = appliance.status === APPLIANCE_STATUS.STOPPED;
  const dispatch = useDispatch();

  const methods = useForm({
    resolver: reduxFormResolver(validate),
    defaultValues: initialValues || { devicesGroup: [] },
    mode: 'onChange'
  });

  const { trigger, handleSubmit, formState: { isDirty }, reset } = methods;

  useEffect(() => {
    if (initialValues) {
      reset(initialValues);
    }
  }, [initialValues, reset]);

  useEffect(() => () => {
    dispatch({ type: RESET_APPLIANCE_FORM_DATA });
  }, [dispatch, applianceId]);

  const errorContextValue = useMemo(() => ({ errors, setErrors }), [errors, setErrors]);

  const validateStep = async currentStep => {
    const fieldsToValidate = currentStep === 0 ? ['label', 'warehouse'] : ['devicesGroup'];
    const isStepValid = await trigger(fieldsToValidate);
    setForceError(!isStepValid);
    return isStepValid;
  };

  const nextStep = async () => {
    const isStepValid = await validateStep(step);
    if (isStepValid) dispatch({ type: GO_TO_APPLIANCE_STEP, payload: step + 1 });
  };

  const onPreviousStep = async () => {
    const isStepValid = await validateStep(step);
    if (isStepValid) dispatch({ type: GO_TO_APPLIANCE_STEP, payload: step - 1 });
  };

  const previousStep = () => {
    if (applianceId) {
      onPreviousStep();
    } else {
      dispatch({ type: GO_TO_APPLIANCE_STEP, payload: step - 1 });
    }
  };

  const handleStepClick = index => async () => {
    const isStepValid = await validateStep(step);
    if (isStepValid) dispatch(goToApplianceStep(index));
  };

  const onSubmitFail = errorsData => {
    if (errors.devicesGroup) {
      const devicesGroupErrors =
        errorsData?.devicesGroup?.map(error => !!Object.values(error || {}).length) ?? [];
      setErrors(s => ({ ...s, devicesGroup: devicesGroupErrors }));
    }
  };

  const getStepProps = () => {
    const stepProps = {};
    if (applianceId && !isDirty) {
      stepProps.completed = true;
    }
    return stepProps;
  };

  const onApplianceSubmit = async data => {
    dispatch({ type: OPEN_BACKDROP });
    const formData = getSubmitData(data);
    await handleFormSubmit(formData);
    dispatch({ type: CLOSE_BACKDROP });
  };

  return (
    <Container>
      <StyledStepper
        alternativeLabel
        nonLinear={!!applianceId}
        activeStep={step}
      >
        {steps.map(({ label, form }, index) => (
          <StyledStep key={label} {...getStepProps(form)}>
            <StepButton disabled={!applianceId || index === step} onClick={handleStepClick(index)}>
              <StepLabel StepIconComponent={applianceId ? CustomStepIcon : StepIcon}>
                <span style={{ fontWeight: index === step ? 500 : 400 }}>{label}</span>
              </StepLabel>
            </StepButton>
          </StyledStep>
        ))}
      </StyledStepper>
      <ErrorsContext.Provider value={errorContextValue}>
        <FormProvider {...methods}>
          {step === 0 &&
            <ApplianceForm
              {...props}
              applianceId={applianceId}
              nextStep={nextStep}
              onSubmit={handleSubmit(onApplianceSubmit, onSubmitFail)}
              setForceError={setForceError}
              forceError={forceError}
              isApplianceStop={isApplianceStop}
              isDirty={isDirty}
            />}
          {step === 1 &&
            <DevicesForm
              {...props}
              applianceId={applianceId}
              onSubmitFail={onSubmitFail}
              onSubmit={handleSubmit(onApplianceSubmit, onSubmitFail)}
              previousStep={previousStep}
              nextStep={nextStep}
              setForceError={setForceError}
              forceError={forceError}
              isApplianceStop={isApplianceStop}
              alerts={appliance?.alerts}
              isDirty={isDirty}
            />}
        </FormProvider>
      </ErrorsContext.Provider>
    </Container>
  );
};

export default Appliance;
